import { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, ChevronLeft, ChevronRight, Loader2, RefreshCw, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${TOKEN()}` });

const HOUR_START = 7;   // 07:00
const HOUR_END = 22;    // 22:00
const PX_PER_MIN = 1.2; // 72px per hour
const TIMELINE_HEIGHT = (HOUR_END - HOUR_START) * 60 * PX_PER_MIN;

const TYPE_LABELS = {
  pickup: "Pick-up", delivery: "Delivery", trial: "Trial",
  dropoff: "Drop-off", hold: "Reserved",
};

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseHM(t) {
  const [h, m] = String(t || "00:00").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function fmt12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function AdminCalendar() {
  const [view, setView] = useState("day"); // "day" | "week"
  const [anchor, setAnchor] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBlock, setSelectedBlock] = useState(null);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[AdminCalendar] mounted, API base:", API);
  }, []);

  const anchorStr = useMemo(() => ymd(anchor), [anchor]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    // eslint-disable-next-line no-console
    console.log(`[AdminCalendar] fetching ${API}/booking/admin/calendar?date=${anchorStr}&view=${view}`);
    try {
      const res = await axios.get(`${API}/booking/admin/calendar`, {
        params: { date: anchorStr, view },
        headers: headers(),
      });
      // eslint-disable-next-line no-console
      console.log("[AdminCalendar] data received:", res.data?.days?.length, "days,", res.data?.days?.[0]?.blocks?.length, "blocks on first day");
      setData(res.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[AdminCalendar] fetch failed:", e?.response?.status, e?.response?.data || e?.message);
      toast.error(`Failed to load calendar (${e?.response?.status || "network"})`);
    } finally {
      setLoading(false);
    }
  }, [anchorStr, view]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shift = (direction) => {
    const d = new Date(anchor);
    const step = view === "week" ? 7 : 1;
    d.setDate(d.getDate() + direction * step);
    setAnchor(d);
  };

  const goToday = () => setAnchor(new Date());

  const hours = useMemo(() => {
    const arr = [];
    for (let h = HOUR_START; h <= HOUR_END; h++) arr.push(h);
    return arr;
  }, []);

  const dayLabel = (d) => {
    return d.day_label || new Date(d.date + "T00:00:00").toLocaleDateString("en-AU", {
      weekday: "short", day: "numeric", month: "short",
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-[1400px] mx-auto" data-testid="admin-calendar-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Schedule
            </h1>
            <p className="text-[12px] text-[#676664]/60 mt-0.5">All bookings, all types — single timeline.</p>
          </div>

          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="inline-flex bg-white border border-[#282828]/12 overflow-hidden">
              <button
                onClick={() => setView("day")}
                className={`px-4 py-2 text-[11px] tracking-wider uppercase transition-colors ${view === "day" ? "bg-[#9A7B4F] text-white" : "text-[#676664] hover:bg-[#FAF8F0]"}`}
                data-testid="calendar-view-day-btn"
              >Day</button>
              <button
                onClick={() => setView("week")}
                className={`px-4 py-2 text-[11px] tracking-wider uppercase transition-colors ${view === "week" ? "bg-[#9A7B4F] text-white" : "text-[#676664] hover:bg-[#FAF8F0]"}`}
                data-testid="calendar-view-week-btn"
              >Week</button>
            </div>

            {/* Date controls */}
            <button onClick={() => shift(-1)} className="p-2 bg-white border border-[#282828]/12 hover:border-[#9A7B4F]/40" data-testid="calendar-prev-btn">
              <ChevronLeft className="w-4 h-4 text-[#676664]" />
            </button>
            <button onClick={goToday} className="px-3 py-2 bg-white border border-[#282828]/12 text-[11px] tracking-wider uppercase text-[#676664] hover:border-[#9A7B4F]/40" data-testid="calendar-today-btn">
              Today
            </button>
            <button onClick={() => shift(1)} className="p-2 bg-white border border-[#282828]/12 hover:border-[#9A7B4F]/40" data-testid="calendar-next-btn">
              <ChevronRight className="w-4 h-4 text-[#676664]" />
            </button>
            <button onClick={fetchData} className="p-2 bg-white border border-[#282828]/12 hover:border-[#9A7B4F]/40" data-testid="calendar-refresh-btn">
              <RefreshCw className={`w-4 h-4 text-[#676664] ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mb-4 flex-wrap" data-testid="calendar-legend">
          {Object.entries(TYPE_LABELS).map(([k, label]) => (
            <div key={k} className="flex items-center gap-1.5 text-[11px] text-[#676664]">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ background: (data?.type_colors || {})[k] || "#999" }}
              />
              {label}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="bg-white border border-[#282828]/8 overflow-hidden">
          {loading && !data ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
            </div>
          ) : (
            <CalendarGrid
              days={data?.days || []}
              hours={hours}
              onSelectBlock={setSelectedBlock}
              dayLabel={dayLabel}
            />
          )}
        </div>

        {/* Detail panel */}
        {selectedBlock && (
          <BlockDetail block={selectedBlock} onClose={() => setSelectedBlock(null)} />
        )}
      </div>
    </AdminLayout>
  );
}


function CalendarGrid({ days, hours, onSelectBlock, dayLabel }) {
  if (days.length === 0) {
    return <div className="text-center py-20 text-[13px] text-[#676664]/50">No data</div>;
  }

  return (
    <div className="flex" style={{ minHeight: TIMELINE_HEIGHT + 60 }}>
      {/* Hour labels column */}
      <div className="w-16 flex-shrink-0 border-r border-[#282828]/8 pt-[60px]">
        {hours.map((h, i) => (
          <div
            key={h}
            className="text-[10px] text-[#676664]/50 pr-2 text-right relative"
            style={{ height: 60 * PX_PER_MIN }}
          >
            {i < hours.length - 1 && <span className="absolute -top-1.5 right-2">{`${h}:00`}</span>}
          </div>
        ))}
      </div>

      {/* Day columns */}
      <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(140px, 1fr))` }}>
        {days.map((day) => (
          <div key={day.date} className="border-r border-[#282828]/8 last:border-r-0" data-testid={`calendar-day-${day.date}`}>
            {/* Day header */}
            <div className="h-[60px] px-3 py-2 border-b border-[#282828]/8 bg-[#FAF8F0]/60 flex flex-col justify-center">
              <span className="text-[10px] tracking-wider uppercase text-[#9A7B4F]">
                {day.day_of_week?.slice(0, 3)}
              </span>
              <span className="text-[13px] text-[#1A1A1A] font-medium">{dayLabel(day)}</span>
            </div>

            {/* Timeline body */}
            <div className="relative" style={{ height: TIMELINE_HEIGHT }}>
              {/* Hour grid lines */}
              {hours.slice(0, -1).map((h, i) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-dashed border-[#282828]/8"
                  style={{ top: i * 60 * PX_PER_MIN }}
                />
              ))}

              {/* Blocks */}
              {(day.blocks || []).map((b) => {
                const startM = parseHM(b.start_time) - HOUR_START * 60;
                const endM = parseHM(b.effective_end) - HOUR_START * 60;
                if (endM <= 0 || startM >= (HOUR_END - HOUR_START) * 60) return null;
                const top = Math.max(0, startM) * PX_PER_MIN;
                const height = Math.max(20, (endM - startM) * PX_PER_MIN);
                const isHold = b.type === "hold";

                return (
                  <button
                    key={b.id}
                    onClick={() => onSelectBlock(b)}
                    className="absolute left-1 right-1 px-2 py-1 text-left text-white text-[11px] hover:opacity-90 transition-opacity shadow-sm overflow-hidden"
                    style={{
                      top, height,
                      background: b.color,
                      borderLeft: isHold ? "3px dashed rgba(255,255,255,0.5)" : "3px solid rgba(255,255,255,0.35)",
                      opacity: isHold ? 0.85 : 1,
                    }}
                    data-testid={`calendar-block-${b.id}`}
                    title={`${TYPE_LABELS[b.type] || b.type} — ${b.customer_name}`}
                  >
                    <div className="font-medium leading-tight">
                      {fmt12(b.start_time)} · {TYPE_LABELS[b.type] || b.type}
                    </div>
                    {height > 32 && (
                      <div className="opacity-80 truncate">{b.customer_name || "—"}</div>
                    )}
                    {height > 50 && b.travel_buffer > 0 && (
                      <div className="opacity-60 text-[10px] mt-0.5">+{b.travel_buffer}m travel</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function BlockDetail({ block, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="calendar-block-detail">
      <div
        className="bg-white max-w-md w-full p-6 border-t-4 shadow-xl"
        style={{ borderTopColor: block.color }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-wider uppercase text-[#9A7B4F] mb-1">
              {TYPE_LABELS[block.type] || block.type}
            </p>
            <h3 className="text-xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              {block.customer_name || "Reserved"}
            </h3>
          </div>
          <button onClick={onClose} className="text-[#676664]/60 hover:text-[#282828] text-2xl leading-none" data-testid="calendar-block-detail-close">×</button>
        </div>

        <div className="space-y-2.5 text-[13px] text-[#5A5A5A]">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#9A7B4F]/60" />
            <span>{new Date(block.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#9A7B4F]/60" />
            <span>{fmt12(block.start_time)} – {fmt12(block.end_time)} ({block.duration_minutes} min)</span>
          </div>
          {block.travel_buffer > 0 && (
            <div className="text-[12px] text-[#676664]/60 pl-6">+ {block.travel_buffer} min travel buffer (blocks until {fmt12(block.effective_end)})</div>
          )}
          {block.customer_email && (
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#9A7B4F]/60" />
              <a href={`mailto:${block.customer_email}`} className="hover:underline">{block.customer_email}</a>
            </div>
          )}
          {block.customer_phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#9A7B4F]/60" />
              <a href={`tel:${block.customer_phone}`} className="hover:underline">{block.customer_phone}</a>
            </div>
          )}
          {block.delivery_address && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#9A7B4F]/60 mt-0.5" />
              <span>{block.delivery_address}</span>
            </div>
          )}
          {block.notes && (
            <div className="pt-2 border-t border-[#282828]/8 text-[12px] text-[#676664]/80">{block.notes}</div>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-[#282828]/8 flex items-center justify-between text-[11px] text-[#676664]/50">
          <span className="uppercase tracking-wider">Status: {block.status}</span>
          <span>ID: {block.id?.slice(0, 12)}…</span>
        </div>
      </div>
    </div>
  );
}
